import React, { Component, type ErrorInfo, type ReactNode } from 'react'

import ErrorBoundaryMessage from 'src/components/ErrorBoundaryMessage/ErrorBoundaryMessage'

import AppInsightsContext from '../context/AppInsightsContext'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode | ((error: Error, resetError: () => void) => ReactNode)
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  trackingId?: string
  captureStackTrace?: boolean
  fullPage?: boolean
  showSupportOption?: boolean
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static contextType = AppInsightsContext
  declare context: React.ContextType<typeof AppInsightsContext>

  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null
    }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Track the error in Application Insights
    const { trackingId, captureStackTrace = true, onError } = this.props

    // Create properties for AI
    const properties = {
      componentStack: captureStackTrace ? errorInfo.componentStack : 'Stack trace disabled',
      errorMessage: error.message,
      errorName: error.name,
      errorBoundaryId: trackingId || 'global',
      url: window.location.href,
      path: window.location.pathname
    }

    // Get App Insights from context and track exception
    if (this.context && this.context.appInsights) {
      this.context.appInsights.trackException({
        exception: error,
        properties
      })

      // Also track as a custom event for easier querying
      this.context.capture('react_error', {
        ...properties,
        stack: error.stack
      })
    }

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo)
    }
  }

  resetError = (): void => {
    this.setState({ hasError: false, error: null })
  }

  render(): ReactNode {
    const { hasError, error } = this.state
    const { children, fallback, fullPage, showSupportOption } = this.props

    if (hasError && error) {
      if (typeof fallback === 'function') {
        return fallback(error, this.resetError)
      }

      if (fallback) {
        return fallback
      }

      return <ErrorBoundaryMessage error={error} resetError={this.resetError} fullPage={fullPage} showSupportOption={showSupportOption} />
    }

    return children
  }
}
